/**
 * ASCII Hero Engine - Scene Presets and Factory Helpers
 * Ready-to-use scene configurations from hero.html and timeline-example.html
 */

import { DEFAULT_MID_GREY_MASK, PROJECT_SHOWCASE_MID_GREY_MASK } from "../constants.js";
import { AsciiIllustration } from "../illustrations/AsciiIllustration.js";
import { AsciiBlurRectangle } from "../illustrations/AsciiBlurRectangle.js";
import { AsciiConcentricCircles } from "../illustrations/AsciiConcentricCircles.js";
import { AsciiHeroEngine } from "../engine/AsciiHeroEngine.js";
import { AsciiTimeline } from "../timeline/AsciiTimeline.js";

/**
 * Slide 1 Preset: Side Profile with Solar Star & Frosted Glass Card
 * @param {Object} overrides
 * @returns {Object} Scene definition
 */
export function createSideProfileScene(overrides = {}) {
  const image = overrides.image || "./side-profile-final.webp";
  return Object.assign(
    {
      id: "slide-profile",
      title: "Side Profile",
      duration: 7500,
      accentColor: "#ff7120",
      image: image,
      midGreyMask: overrides.midGreyMask !== undefined ? overrides.midGreyMask : DEFAULT_MID_GREY_MASK,
      config: {
        midGreyColor: "#444444",
        bgOpacity: overrides.bgOpacity !== undefined ? overrides.bgOpacity : 0.85,
        dustCount: 25,
      },
      illustrations: [
        new AsciiIllustration({
          name: "hero-star",
          anchor: "portrait",
          u: 0.72,
          v: 0.75,
          size: 110,
          color: "#FF7120",
          asciiColor: "#FF7120",
          fontSize: 38,
          lineHeight: 42,
          charWidth: 30,
          rotationSpeed: 0.008,
          hoverFloating: true,
          hoverStopMotion: true,
          hoverFps: 8,
          hoverFloatAmplitude: 0,
          hoverJitter: 2.2,
          hoverRotationSpeed: 0.008,
          depth: 0.85,
          layer: "background",
        }),
        /* new AsciiBlurRectangle({
          name: "frosted-card",
          anchor: "portrait",
          u: 0.65,
          v: 0.7,
          width: 360,
          height: 300,
          borderRadius: 4,
          blur: 12,
          tint: "rgba(0, 0, 0, 0.025)",
          borderColor: "rgba(255, 255, 255, 0.2)",
          borderWidth: 0,
          depth: 0.65,
          layer: "foreground",
        }), */ 
      ],
    },
    overrides
  );
}

/**
 * Slide 2 Preset (Hero Version): Project Showcase with Cyber Mint Rings & HUD
 * @param {Object} overrides
 * @returns {Object} Scene definition
 */
export function createHeroShowcaseScene(overrides = {}) {
  const image = overrides.image || "./project-showcase-final.webp";
  return Object.assign(
    {
      id: "slide-project",
      title: "Project Showcase",
      duration: 8000,
      accentColor: "#ff7120",
      image: image,
      midGreyMask: overrides.midGreyMask !== undefined ? overrides.midGreyMask : PROJECT_SHOWCASE_MID_GREY_MASK,
      config: {
        midGreyColor: "#00f59b", // Cyber Mint accent
        bgOpacity: 0.88,
        dustCount: 30,
      },
      illustrations: [
        new AsciiConcentricCircles({
          name: "showcase-rings",
          anchor: "portrait",
          u: 0.68,
          v: 0.6,
          ringCount: 8,
          baseRadius: 36,
          initialStep: 22,
          stepDecay: 0.14,
          baseLag: 0.28,
          lagDecay: 0.22,
          followMode: "magnetic",
          magneticPull: 0.35,
          strokeColor: "#00f59b",
          strokeWidth: 1.5,
          asciiOnHover: true,
          depth: 0.75,
          layer: "foreground",
        }),
        new AsciiBlurRectangle({
          name: "showcase-glass-badge",
          anchor: "portrait",
          u: 0.68,
          v: 0.6,
          width: 290,
          height: 120,
          borderRadius: 8,
          blur: 16,
          tint: "rgba(0, 245, 155, 0.04)",
          borderColor: "rgba(0, 245, 155, 0.3)",
          borderWidth: 1,
          depth: 0.65,
          layer: "foreground",
        }),
      ],
    },
    overrides
  );
}

/**
 * Slide 2 Preset (Timeline Example Version): Project Showcase with subtle background rings & glass badge
 * @param {Object} overrides
 * @returns {Object} Scene definition
 */
export function createTimelineShowcaseScene(overrides = {}) {
  const image = overrides.image || "./project-showcase-final.webp";
  return Object.assign(
    {
      id: "slide-project",
      title: "Project Showcase",
      duration: 8000,
      accentColor: "#ff7120",
      image: image,
      midGreyMask: overrides.midGreyMask !== undefined ? overrides.midGreyMask : PROJECT_SHOWCASE_MID_GREY_MASK,
      config: {
        midGreyColor: "#ff7120",
        bgOpacity: 0.95,
        dustCount: 30,
      },
      illustrations: [
        new AsciiConcentricCircles({
          name: "showcase-rings",
          anchor: "portrait",
          u: 0.0,
          v: 0.0,
          ringCount: 5,
          baseRadius: 36,
          initialStep: 12,
          stepDecay: 0.35,
          baseLag: 0.28,
          lagDecay: 0.35,
          followMode: "magnetic",
          magneticPull: 0.05,
          strokeColor: "rgba(255, 255, 255, 0.1)",
          strokeWidth: 1.5,
          asciiOnHover: false,
          depth: 0.75,
          layer: "background",
        }),
        new AsciiBlurRectangle({
          name: "showcase-glass-badge",
          anchor: "portrait",
          u: 0.0,
          v: 0.1,
          width: 290,
          height: 120,
          borderRadius: 8,
          blur: 8,
          tint: "rgba(255, 255, 255, 0.05)",
          borderColor: "rgba(255, 255, 255, 0.3)",
          borderWidth: 0,
          depth: 0.65,
          layer: "foreground",
        }),
      ],
    },
    overrides
  );
}

/**
 * Quick initializer for hero.html scene setup
 * @param {string|HTMLCanvasElement} canvasSelector
 * @param {Object} options
 * @returns {{ engine: AsciiHeroEngine, timeline: AsciiTimeline }}
 */
export function initHeroPreset(canvasSelector = "#canvas", options = {}) {
  const engine = new AsciiHeroEngine(
    Object.assign(
      {
        charWidth: 22,
        lineHeight: 22,
        fontSize: 16,
        spotlightRadius: 80,
        spotlightPower: 1.0,
        spotlightAttackSpeed: 0.16,
        spotlightDecaySpeed: 0.048,
        mouseLag: 0.09,
        parallaxAmount: 0,
        bgOpacity: 0.85,
        midGreyColor: "#dddddd",
        midGreyColorRGB: [68, 68, 68],
        glitchSpeed: 0.0005,
        glitchRate: 0.004,
        dustCount: 25,
        dustSpeed: 0.5,
        dustFontSize: 26,
        dustStopMotion: true,
        dustFps: 8,
        dustJitter: 1.6,
        dustMouseInteraction: false,
      },
      options.engineConfig
    )
  );

  const initialImage = options.initialImage || "./side-profile-final.webp";
  engine.mount(canvasSelector, initialImage);

  const scenes = options.scenes || [
    createSideProfileScene({ image: initialImage, bgOpacity: 0.85 }),
    createHeroShowcaseScene({ image: options.showcaseImage || "./project-showcase-final.webp" }),
  ];

  const timeline = new AsciiTimeline(
    Object.assign(
      {
        autoPlay: true,
        loop: true,
        defaultDuration: 7500,
        defaultTransition: 1200,
        scenes: scenes,
      },
      options.timelineConfig
    )
  );

  engine.setTimeline(timeline);
  if (options.mountControls !== false) {
    timeline.mountControls(options.controlsContainer || (typeof document !== "undefined" ? document.body : null));
  }

  return { engine, timeline };
}

/**
 * Quick initializer for timeline-example.html scene setup
 * @param {string|HTMLCanvasElement} canvasSelector
 * @param {Object} options
 * @returns {{ engine: AsciiHeroEngine, timeline: AsciiTimeline }}
 */
export function initTimelinePreset(canvasSelector = "#canvas", options = {}) {
  const engine = new AsciiHeroEngine(
    Object.assign(
      {
        charWidth: 22,
        lineHeight: 22,
        fontSize: 16,
        spotlightRadius: 80,
        spotlightPower: 1.0,
        spotlightAttackSpeed: 0.16,
        spotlightDecaySpeed: 0.048,
        mouseLag: 0.09,
        parallaxAmount: 0,
        bgOpacity: 0.75,
        midGreyColor: "#dddddd",
        midGreyColorRGB: [68, 68, 68],
        glitchSpeed: 0.0005,
        glitchRate: 0.004,
        dustCount: 25,
        dustSpeed: 0.5,
        dustFontSize: 26,
        dustStopMotion: true,
        dustFps: 8,
        dustJitter: 1.6,
        dustMouseInteraction: false,
      },
      options.engineConfig
    )
  );

  const initialImage = options.initialImage || "./side-profile-final.webp";
  engine.mount(canvasSelector, initialImage);

  const scenes = options.scenes || [
    createSideProfileScene({ image: initialImage, bgOpacity: 0.75 }),
    createTimelineShowcaseScene({ image: options.showcaseImage || "./project-showcase-exp.png" }),
  ];

  const timeline = new AsciiTimeline(
    Object.assign(
      {
        autoPlay: true,
        loop: true,
        defaultDuration: 7500,
        defaultTransition: 2000,
        scenes: scenes,
      },
      options.timelineConfig
    )
  );

  engine.setTimeline(timeline);
  if (options.mountControls !== false) {
    timeline.mountControls(options.controlsContainer || (typeof document !== "undefined" ? document.body : null));
  }

  return { engine, timeline };
}
